import type { ClearableDropdownOption, DropdownOption } from 'types/ui'
import type { User } from 'types/user'

import { getUserDisplayName } from '@disco/utils/string-utils'

/**
 * Validates if a user object has sufficient information to be considered valid.
 *
 * Checks if the user has at least one of: first name, last name, or email address.
 *
 * @param user - The user object to validate
 * @returns True if the user has at least one identifying field
 *
 * @example
 * ```typescript
 * const validUser = { first_name: 'John', last_name: '', email: '' }
 * isValidUser(validUser) // returns true
 *
 * const invalidUser = { first_name: '', last_name: '', email: '' }
 * isValidUser(invalidUser) // returns false
 *
 * const emailOnlyUser = { first_name: '', last_name: '', email: 'user@example.com' }
 * isValidUser(emailOnlyUser) // returns true
 * ```
 */
export function isValidUser(user: User): boolean {
  const { first_name, last_name, email } = user
  return (
    Boolean(first_name?.trim()) ||
    Boolean(last_name?.trim()) ||
    Boolean(email?.trim())
  )
}

/**
 * Converts a user object into a dropdown option with clearable functionality.
 *
 * Creates a dropdown option object suitable for use in select components,
 * with optional locking to prevent removal.
 *
 * @param user - The user object to convert
 * @param locked - Whether the option should be locked (non-clearable)
 * @returns Dropdown option object with user data
 *
 * @example
 * ```typescript
 * const user = { id: 123, first_name: 'John', last_name: 'Doe' }
 *
 * const unlocked = getUserDropdownObject(user, false)
 * // { value: 123, label: 'John Doe', clearableValue: true }
 *
 * const locked = getUserDropdownObject(user, true)
 * // { value: 123, label: 'John Doe', clearableValue: false }
 * ```
 */
export function getUserDropdownObject(
  user: User,
  locked = false,
): ClearableDropdownOption {
  return {
    value: user.id,
    label: getUserDisplayName(user),
    clearableValue: !locked,
  }
}

/**
 * Converts a dropdown option to a locked (non-clearable) state.
 *
 * Takes an existing dropdown option and returns a new version that
 * cannot be cleared/removed by the user.
 *
 * @param dropdownObject - The dropdown option to lock
 * @returns Locked dropdown option (clearableValue: false)
 *
 * @example
 * ```typescript
 * const option = { value: 123, label: 'John Doe', clearableValue: true }
 * const locked = convertUserDropdownObjectToLocked(option)
 * // { value: 123, label: 'John Doe', clearableValue: false }
 * ```
 */
export function convertUserDropdownObjectToLocked(
  dropdownObject: DropdownOption,
): ClearableDropdownOption {
  return { ...dropdownObject, clearableValue: false }
}

/**
 * Converts a dropdown option to an unlocked (clearable) state.
 *
 * Takes an existing dropdown option and returns a new version that
 * can be cleared/removed by the user.
 *
 * @param dropdownObject - The dropdown option to unlock
 * @returns Unlocked dropdown option (clearableValue: true)
 *
 * @example
 * ```typescript
 * const option = { value: 123, label: 'John Doe', clearableValue: false }
 * const unlocked = convertUserDropdownObjectToUnlocked(option)
 * // { value: 123, label: 'John Doe', clearableValue: true }
 * ```
 */
export function convertUserDropdownObjectToUnlocked(
  dropdownObject: DropdownOption,
): ClearableDropdownOption {
  return { ...dropdownObject, clearableValue: true }
}
